// @flow weak

import { ROUTE_TO_NEXT, routeTo } from 'redux-router-kit';

import { RouterSelectors } from '@zapier/toolbox-redux-framework';
import {
  addTruthyParamsToUrl,
  extractParams,
  urlWithNext,
} from '@zapier/url-utils';

import { Account, Profile, Onboarding } from '@zapier/shared-entities';

import isAuthRequiredForMatchedRoutes from '../utils/isAuthRequiredForMatchedRoutes';
import isAuthAttemptRequiredForMatchedRoutes from './utils/isAuthAttemptRequiredForMatchedRoutes';

const forceLoadAccountAndProfile = store =>
  Promise.all([
    store.dispatch(Account.actions.forceLoadCollection()),
    store.dispatch(Profile.actions.forceLoadCollection()),
    store.dispatch(Onboarding.actions.forceLoadCollection()),
  ]);

const createAuthenticationMiddleware = ({ routes }) => {
  const middleware = store => next => action => {
    if (!action) {
      return next(action);
    }

    if (action.type === ROUTE_TO_NEXT) {
      const state = store.getState();
      const hasCurrentAccount = !!Account.selectors.all.currentAccount(state);
      const hasCurrentProfile = !!Profile.selectors.all.currentProfile(state);
      const { routeKey } = action.meta;
      const isAuthRequired = isAuthRequiredForMatchedRoutes(routes, routeKey);
      const isAuthAttemptRequired = isAuthAttemptRequiredForMatchedRoutes(
        routes,
        routeKey
      );

      if (!(hasCurrentAccount && hasCurrentProfile)) {
        if (!isAuthRequired) {
          // When auth isn't required for the route, still attempt to
          // load account and profile data. If `isAuthAttemptRequired`
          // then wait until loading has completed (successfully or not)
          // before routing, otherwise route "immediately".
          const loadingPromise = forceLoadAccountAndProfile(store);
          return (isAuthAttemptRequired
            ? loadingPromise
            : Promise.resolve()
          ).then(() => {
            return next(action);
          });
        }

        // Need to `forceLoadCollection` because `shouldLoad` entity selector
        // could prevent the collection from loading if it's *already* loading,
        // which would immediately go to `then` before collection actually
        // finished loading, causing `isSuccess` to be `false` before it has
        // been accurately determined.
        return forceLoadAccountAndProfile(store).then(() => {
          const isAuthenticated =
            Account.selectors.all.isSuccess(store.getState()) &&
            Profile.selectors.all.isSuccess(store.getState());
          return isAuthRequired && !isAuthenticated
            ? store.dispatch(
                routeTo(urlWithNext('/app/login', action.meta.url))
              )
            : next(action);
        });
      }
    }

    // Check if there was an auth-realm violation (SAML vs regular credentials)
    if (
      // catches direct API call errors
      (action?.error?.status === 403 &&
        action?.error?.json?.reason === 'invalid_auth_realm') ||
      // catches an Entity API violations
      (action?.statusCode === 403 && action?.reason === 'invalid_auth_realm')
    ) {
      // There was a violation so force the user to reauthenticate
      const url = RouterSelectors.currentUrl(store.getState());
      const params = extractParams(url);

      if (!params.reauth) {
        // We only want to dispatch this route if
        // we aren't already in a reauth flow (otherwise we could
        // potentially trigger this multiple times)
        return store.dispatch(
          routeTo(
            addTruthyParamsToUrl(urlWithNext('/app/login', url), {
              reauth: true,
            })
          )
        );
      }
    }

    return next(action);
  };

  middleware.setRoutes = newRoutes => {
    routes = newRoutes;
  };

  return middleware;
};

export default createAuthenticationMiddleware;
